import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';

import { CarbonFootprint } from '../models/carbon-footprint.model';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root'
})
export class CarbonFootprintService {

  private baseUrl: string = '/:apiBase/companies/:companyId/carbon-footprint/:targetCompanyId';

  /** Minutos. connect-back y huella-back también cachean del lado suyo. */
  private readonly CACHE_TTL = 60;

  constructor(
    private http: HttpClient,
    private cacheService: CacheService
  ) { }

  /**
   * Huella de carbono de la empresa `targetCompanyId` en la campaña activa.
   *
   * Cachea por URL y comparte el request en vuelo: una misma empresa suele aparecer
   * en varias contraórdenes a la vez. Ante error devuelve `null` (sin badge) y no
   * cachea, para reintentar en el próximo render.
   */
  public get(companyId: number, targetCompanyId: number): Observable<CarbonFootprint | null> {
    const url = this.baseUrl
      .replace(':companyId', companyId.toString())
      .replace(':targetCompanyId', targetCompanyId.toString());

    const cached: CarbonFootprint = this.cacheService.get(url);
    if (cached) return of(cached);

    const inFlight = this.cacheService.getInFlight(url);
    if (inFlight) return inFlight;

    const request$ = this.http.get<CarbonFootprint>(url).pipe(
      tap(data => this.cacheService.set(url, data, this.CACHE_TTL)),
      catchError(() => of(null)),
      shareReplay(1),
      finalize(() => this.cacheService.clearInFlight(url))
    );

    this.cacheService.setInFlight(url, request$);
    return request$;
  }
}
